import './Onboarding.component.scss';
import 'swiper/css/swiper.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swiper, { ReactIdSwiperProps } from 'react-id-swiper';
import { FormattedMessage } from 'react-intl';

import contacts from '../../../assets/icons/CONTACTOS_azul.svg';
import personal from '../../../assets/icons/DATOSPERSONALES_azul.svg';
import yellowDecor from '../../../assets/icons/ELEMENTOS_DECO.svg';
import groups from '../../../assets/icons/GRUPOS_azul.svg';
import liliHome from '../../../assets/icons/LILI_HOME.svg';
import wishlist from '../../../assets/icons/LISTADESEOS_azul.svg';
import account from '../../../assets/icons/MICUENTA_azul.svg';
import present from '../../../assets/icons/REGALO_MAGICO.svg';
import history from '../../../store/history/history';
import { NavbarComponent } from '../../organisms/organisms.index';
import { useSelector } from 'react-redux';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import { SpinnerComponent } from '../../atoms/atoms.index';

const listItems = [
  {
    img: wishlist,
    title: (
      <FormattedMessage id='pages.home.wishlist'>{txt => txt}</FormattedMessage>
    ),
    description: (
      <FormattedMessage id='pages.onboarding.description.wishlist'>
        {txt => txt}
      </FormattedMessage>
    ),
  },
  {
    img: groups,
    title: (
      <FormattedMessage id='pages.home.groups'>{txt => txt}</FormattedMessage>
    ),
    description: (
      <FormattedMessage id='pages.onboarding.description.groups'>
        {txt => txt}
      </FormattedMessage>
    ),
  },
  {
    img: contacts,
    title: (
      <FormattedMessage id='pages.home.contacts'>{txt => txt}</FormattedMessage>
    ),
    description: (
      <FormattedMessage id='pages.onboarding.description.contacts'>
        {txt => txt}
      </FormattedMessage>
    ),
  },
  {
    img: account,
    title: (
      <FormattedMessage id='pages.home.my-bank'>{txt => txt}</FormattedMessage>
    ),
    description: (
      <FormattedMessage id='pages.onboarding.description.my-bank'>
        {txt => txt}
      </FormattedMessage>
    ),
  },
  {
    img: personal,
    title: (
      <FormattedMessage id='pages.home.personal'>{txt => txt}</FormattedMessage>
    ),
    description: (
      <FormattedMessage id='pages.onboarding.description.personal'>
        {txt => txt}
      </FormattedMessage>
    ),
  },
];

function OnboardingPage(props: { index: number }) {
  const { index } = props;
  const item = listItems[index];

  return (
    <div className='swiper-slide page'>
      <div className='card-container'>
        <section className='home-header d-flex flex-row flex-nowrap justify-content-start align-items-center'>
          <h2 className='home-header-text'>
            <FormattedMessage id='pages.home.title' />
          </h2>
          <img className='home-header-picture' src={liliHome} alt='' />
        </section>
        <section className='home-menu d-flex justify-content-around align-items-center mt-1 mt-sm-2'>
          <div className='home-list-container'>
            {listItems.map((it, idx) => (
              <div
                className={`home-menu-item-${idx + 1} ${
                  idx === index ? 'active' : ''
                }`}
                key={`home-menu-item-${idx + 1}`}
              >
                <img className='card-image' src={it.img} alt='img' />
                <h4 className='card-h4'>{it.title}</h4>
              </div>
            ))}
          </div>
        </section>
        <div className='home-banner-container'>
          <article className='home-banner d-flex flex-row flex-nowrap justify-content-between align-items-center ml-5'>
            <section className='home-banner-left d-flex flex-row flex-nowrap align-items-center'>
              <img className='home-image-deco' src={yellowDecor} alt='' />
              <div className='banner-text'>
                <FormattedMessage id='pages.home.banner' />
              </div>
            </section>
            <section className='home-banner-right'>
              <img className='home-image-present' src={present} alt='' />
            </section>
          </article>
        </div>
        <div className='description-container'>
          <h6 className='subtitle'>{item.title}</h6>
          <p className='text-primary'>{item.description}</p>
        </div>
      </div>
    </div>
  );
}

function OnboardingSkip(props: { currentIndex: number; path: string }) {
  const { currentIndex, path } = props;

  useEffect(() => {
    if (currentIndex === listItems.length) {
      history.push(path);
    }
    // eslint-disable-next-line
  }, [currentIndex]);

  return <div className='swiper-slide'></div>;
}

export function OnboardingComponent() {
  const path = history.location.pathname.replace('/onboarding', '');

  const swiperRef = useRef(null);
  const [currentIndex, updateCurrentIndex] = useState(0);

  const currentUser = useSelector(makeSelectCurrentUser());

  const params: ReactIdSwiperProps = {
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets',
      clickable: true,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    spaceBetween: 10,
    // loop: true,
    slidesPerView: 1.25,
    centeredSlides: true,
  };

  const updateIndex = useCallback(
    () => updateCurrentIndex((swiperRef as any).current.swiper.realIndex),
    [],
  );

  // Add eventlisteners for swiper after initializing
  useEffect(() => {
    const swiperInstance = (swiperRef as any).current.swiper;

    if (swiperInstance) {
      swiperInstance.on('slideChange', updateIndex);
    }

    return () => {
      if (swiperInstance) {
        swiperInstance.off('slideChange', updateIndex);
      }
    };
  }, [updateIndex]);

  return (
    <div>
      {currentUser.legalConditionsAccepted ? (
        <React.Fragment>
          <NavbarComponent
            showName={true}
            showLogout={true}
            showHelp={true}
            hideBack={true}
          />
          <div className='onboarding-page'>
            <button
              className='btn-lili onboarding-skip'
              onClick={() => history.push(path)}
            >
              <FormattedMessage id='common.skip' />
            </button>
            <Swiper {...params} ref={swiperRef}>
              {React.Children.toArray([
                ...listItems.map((item: any, index: number) =>
                  OnboardingPage({ index }),
                ),
                <OnboardingSkip {...{ currentIndex, path }} />,
              ])}
            </Swiper>
          </div>
        </React.Fragment>
      ) : (
        <SpinnerComponent />
      )}
    </div>
  );
}
